import { TysonCoachingContextData } from '../types/coaching';

interface StartCoachingBeforeCallParams {
	companyId: string;
	contactId: string;
	leadServedId?: string;
	phoneCallId?: string;
	startCoaching: (data: TysonCoachingContextData) => Promise<void>;
	getConversationId: () => string | null;
	getIsAIResponding: () => boolean;
	getIsCreatingConversation: () => boolean;
}

/**
 * Utility to start coaching before a call and wait for it to be ready.
 * Returns true if coaching is ready, false if it failed or timed out.
 */
export const startCoachingBeforeCall = async ({
	companyId,
	contactId,
	leadServedId,
	phoneCallId,
	startCoaching,
	getConversationId,
	getIsAIResponding,
	getIsCreatingConversation,
}: StartCoachingBeforeCallParams): Promise<boolean> => {
	if (!companyId || !contactId) {
		console.warn('startCoachingBeforeCall: Missing companyId or contactId');
		return false;
	}

	const contextData: TysonCoachingContextData = {
		companyId,
		contactId,
		leadServedId,
		phoneCallId,
	};

	try {
		await startCoaching(contextData);

		// Wait for conversation to be ready (max 15 seconds for pre-call)
		let attempts = 0;
		const maxAttempts = 15;

		while (attempts < maxAttempts) {
			await new Promise(resolve => setTimeout(resolve, 1000));
			attempts++;

			const conversationId = getConversationId();
			const isResponding = getIsAIResponding();
			const isCreating = getIsCreatingConversation();

			if (conversationId && !isResponding && !isCreating) {
				return true;
			}
		}

		console.warn('startCoachingBeforeCall: Timeout waiting for coaching to initialize');
		return false;
	} catch (error) {
		console.error('startCoachingBeforeCall: Failed to start coaching', error);
		return false;
	}
};
